/* BackupsPanel — super-admin only. UI for the Data Resilience initiative.
 *
 * Lists nightly snapshots, lets admin run one on-demand, download an archive
 * .zip, and run a guarded restore. Heavy ops are confirmed via a typed
 * phrase to prevent accidental data loss.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Database, Download, PlayCircle, Loader2, RefreshCw, AlertTriangle,
  ArchiveRestore, Calendar, ShieldAlert, CheckCircle2, X, Trash2,
  FileArchive, ShieldCheck, FolderArchive,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/backups`;

const fmtBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const BackupsPanel = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [restoreDate, setRestoreDate] = useState(null);
  const [notifyOnDone, setNotifyOnDone] = useState(false);
  const [recipients, setRecipients] = useState(null);

  const loadRecipients = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/alert-recipients`, { withCredentials: true });
      setRecipients(r.data);
    } catch (_) { /* non-blocking */ }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/status`, { withCredentials: true });
      setStatus(r.data);
    } catch (_) { /* non-blocking */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(API, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load backups.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); loadRecipients(); loadStatus(); }, [load, loadRecipients, loadStatus]);

  const handleRun = async () => {
    if (!window.confirm('Run a manual snapshot now? This dumps the database AND archives every uploaded file.')) return;
    setRunning(true); setError('');
    try {
      await axios.post(`${API}/run?notify=${notifyOnDone}&scope=both`, {}, { withCredentials: true });
      await load(); await loadStatus();
    } catch (e) {
      setError(e.response?.data?.detail || 'Snapshot failed.');
    }
    setRunning(false);
  };

  const handleVerify = async (date) => {
    try {
      const r = await axios.post(`${API}/${date}/verify-files`, {}, { withCredentials: true });
      window.alert(r.data.ok
        ? `✓ File archive verified — SHA256 matches manifest.\nVerified at ${r.data.verified_at}.`
        : `⚠ File archive FAILED verification.\nExpected: ${r.data.expected_sha || '(unknown)'}\nActual: ${r.data.actual_sha}`);
      await loadStatus();
    } catch (e) {
      window.alert(`Verification failed: ${e.response?.data?.detail || 'unknown error'}`);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" />
    </div>;
  }

  const policy = data?.policy || {};
  const rows = data?.rows || [];

  return (
    <div className="space-y-4" data-testid="backups-panel">
      {/* Header */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-heading font-bold text-base text-[#0A0A0A] inline-flex items-center gap-2">
              <Database className="w-4 h-4 text-[#BA1826]" /> Automated Backups & Disaster Recovery
            </h2>
            <p className="text-[11px] text-[#4B5563] mt-1 max-w-2xl">
              MongoDB stays the source of truth. Every night the system writes a gzipped JSONL
              snapshot of <strong>{policy.collection_count} operational collections</strong> to
              <code className="mx-1 px-1 bg-[#FAFBFC] border border-[#E5E7EB] rounded text-[10px]">
                /app/backend/uploads/backups/&lt;date&gt;/
              </code>.
              Retention: {policy.daily_retention} daily, {policy.weekly_retention} weekly,
              {policy.monthly_retention} monthly. Older snapshots prune automatically.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] text-[#4B5563] inline-flex items-center gap-1.5"
              title="When checked, you'll get an email confirmation after a clean manual snapshot. Failures always email regardless.">
              <input type="checkbox" checked={notifyOnDone}
                onChange={(e) => setNotifyOnDone(e.target.checked)}
                data-testid="backups-notify-toggle" />
              Email me when done
            </label>
            <button onClick={load}
              className="text-xs px-2.5 py-1.5 text-[#4B5563] hover:text-[#0A0A0A] inline-flex items-center gap-1"
              data-testid="backups-refresh">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
            <button onClick={handleRun} disabled={running}
              className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="backups-run-now">
              {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
              Run snapshot now
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs p-2 rounded inline-flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {error}
        </div>
      )}

      {/* Founder-facing status dashboard — six KPIs with traffic-light accents */}
      {status && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2" data-testid="backups-status-grid">
          <StatusCard label="Database backup" status={status.database.status}
            value={status.database.last_successful ? '✓ Healthy' : 'No snapshot'}
            sub={status.database.last_successful?.date}
            Icon={Database} testid="status-db" />
          <StatusCard label="File backup" status={status.files.status}
            value={status.files.last_successful ? '✓ Healthy' : 'No archive'}
            sub={status.files.last_successful?.date}
            Icon={FolderArchive} testid="status-files" />
          <StatusCard label="Collections protected" status="green"
            value={status.database.collections_protected.toLocaleString()}
            sub={`${status.database.total_runs} total snapshots`}
            Icon={Database} testid="status-collections" />
          <StatusCard label="Files protected" status="green"
            value={(status.files.files_protected || 0).toLocaleString()}
            sub={status.files.last_successful
              ? fmtBytes(status.files.last_successful.compressed_bytes)
              : '—'}
            Icon={FileArchive} testid="status-filecount" />
          <StatusCard label="Last successful backup" status={status.database.status}
            value={status.database.last_successful?.date || '—'}
            sub={status.database.last_successful
              ? fmtBytes(status.database.last_successful.bytes)
              : ''}
            Icon={Calendar} testid="status-last-backup" />
          <StatusCard label="Last verified test" status={status.verification.status}
            value={status.verification.last_verified?.date || 'Not yet run'}
            sub={status.verification.last_verified
              ? new Date(status.verification.last_verified.verified_at).toLocaleString()
              : 'Click Verify on any row'}
            Icon={ShieldCheck} testid="status-last-verify" />
        </div>
      )}

      {/* Alert recipients banner */}
      {recipients && (
        <div className="bg-sky-50 border border-sky-200 rounded p-2.5 text-[11px] text-sky-900"
          data-testid="backups-recipients">
          <strong>Backup alert recipients:</strong>{' '}
          {recipients.recipients.length === 0 ? (
            <span className="text-rose-700 font-semibold">
              ⚠ No recipients configured — failure emails will be skipped.
              Add at least one super-admin or set BACKUP_ALERT_EMAILS.
            </span>
          ) : (
            <>
              {recipients.recipients.join(', ')}
              <span className="text-sky-700 ml-1">
                ({recipients.source})
              </span>
            </>
          )}
        </div>
      )}

      {/* Snapshots table */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-[#E5E7EB] bg-[#FAFBFC]">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">
            Snapshots ({rows.length})
          </h3>
        </div>
        {rows.length === 0 ? (
          <p className="p-6 text-center text-xs text-[#9CA3AF] italic">
            No snapshots yet. Click <strong>Run snapshot now</strong> to create the first one.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[#FAFBFC] text-[10px] uppercase tracking-wider text-[#9CA3AF]">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-right">Documents</th>
                <th className="px-3 py-2 text-right">Size</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.date} className="border-t border-[#F1F5F9]"
                  data-testid={`backup-row-${r.date}`}>
                  <td className="px-3 py-2 font-mono text-[11px] inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-[#9CA3AF]" /> {r.date}
                    {r.errors?.length > 0 && (
                      <span title={r.errors.join('; ')}
                        className="ml-2 text-[10px] text-rose-600 inline-flex items-center gap-0.5">
                        <AlertTriangle className="w-2.5 h-2.5" /> {r.errors.length} error
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{r.total_documents.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtBytes(r.total_bytes)}</td>
                  <td className="px-3 py-2 text-[11px] text-[#4B5563]">
                    {r.manual_by ? `Manual · ${r.manual_by.split('@')[0]}` : 'Scheduled (nightly)'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <a href={`${API}/${r.date}/archive.zip`}
                        className="text-xs px-2 py-1 border border-[#E5E7EB] rounded inline-flex items-center gap-1 hover:bg-[#FAFBFC]"
                        data-testid={`backup-download-${r.date}`}>
                        <Download className="w-3 h-3" /> .zip
                      </a>
                      <button onClick={() => setRestoreDate(r.date)}
                        className="text-xs px-2 py-1 border border-rose-200 text-rose-700 rounded inline-flex items-center gap-1 hover:bg-rose-50"
                        data-testid={`backup-restore-${r.date}`}>
                        <ArchiveRestore className="w-3 h-3" /> Restore
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {restoreDate && (
        <RestoreModal date={restoreDate}
          onClose={() => setRestoreDate(null)}
          onDone={() => { setRestoreDate(null); load(); }} />
      )}
    </div>
  );
};

export default BackupsPanel;

const StatusCard = ({ label, status, value, sub, Icon, testid }) => {
  const colors = {
    green: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', dot: 'bg-emerald-500' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', dot: 'bg-amber-500' },
    red: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', dot: 'bg-rose-500' },
  }[status] || { bg: 'bg-white', border: 'border-[#E5E7EB]', text: 'text-[#0A0A0A]', dot: 'bg-[#9CA3AF]' };
  return (
    <div className={`border rounded p-2.5 ${colors.bg} ${colors.border}`} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} aria-hidden />
        <p className="text-[9px] uppercase tracking-wider font-bold opacity-75">{label}</p>
      </div>
      <div className="flex items-center gap-1.5 mt-1">
        {Icon && <Icon className={`w-3.5 h-3.5 ${colors.text}`} />}
        <p className={`text-base font-bold ${colors.text}`}>{value}</p>
      </div>
      {sub && <p className="text-[10px] text-[#4B5563] mt-0.5 truncate">{sub}</p>}
    </div>
  );
};

const RESTORE_PHRASE = 'RESTORE FROM BACKUP';

const RestoreModal = ({ date, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [phrase, setPhrase] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/${date}/preview`, { withCredentials: true })
      .then((r) => setPreview(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load preview.'));
  }, [date]);

  const toggle = (col) => {
    const next = new Set(selected);
    if (next.has(col)) next.delete(col); else next.add(col);
    setSelected(next);
  };

  const selectAll = () => {
    if (!preview) return;
    setSelected(new Set(preview.rows.map((r) => r.collection)));
  };

  const handleRestore = async () => {
    setRunning(true); setError('');
    try {
      const r = await axios.post(`${API}/${date}/restore`,
        { collections: Array.from(selected), confirm_phrase: phrase },
        { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Restore failed.');
    }
    setRunning(false);
  };

  const canRun = selected.size > 0 && phrase === RESTORE_PHRASE && !running && !result;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="restore-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between bg-rose-50">
          <h3 className="font-heading font-bold text-sm text-rose-800 inline-flex items-center gap-1.5">
            <ShieldAlert className="w-4 h-4" /> Restore from snapshot — {date}
          </h3>
          <button onClick={onClose} className="text-rose-700 hover:text-rose-900">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs p-2 rounded">
              {error}
            </div>
          )}

          {result ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3"
              data-testid="restore-result">
              <p className="text-sm font-bold text-emerald-800 inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> Restore complete
              </p>
              <ul className="mt-2 text-xs text-emerald-700 space-y-0.5">
                {result.summary.map((s) => (
                  <li key={s.collection}>
                    <code>{s.collection}</code> → {s.restored.toLocaleString()} documents
                    {s.error && <span className="text-rose-700"> · {s.error}</span>}
                  </li>
                ))}
              </ul>
              <button onClick={onDone}
                className="mt-3 text-xs px-3 py-1.5 bg-emerald-700 text-white rounded inline-flex items-center gap-1">
                Close
              </button>
            </div>
          ) : !preview ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-rose-600" />
            </div>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs p-2.5 rounded inline-flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  <strong>Heads up:</strong> Restoring will replace the selected collections with
                  the snapshot from <strong>{date}</strong>. Live documents created after that
                  snapshot will be LOST. Pick only the collections you actually need to recover.
                </span>
              </div>

              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-[#0A0A0A]">
                  Choose collections to restore ({selected.size} selected)
                </h4>
                <div className="flex items-center gap-2">
                  <button onClick={selectAll}
                    className="text-[11px] text-[#BA1826] hover:underline"
                    data-testid="restore-select-all">
                    Select all
                  </button>
                  <button onClick={() => setSelected(new Set())}
                    className="text-[11px] text-[#4B5563] hover:underline">
                    Clear
                  </button>
                </div>
              </div>

              <div className="border border-[#E5E7EB] rounded max-h-[300px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFBFC] text-[10px] uppercase tracking-wider text-[#9CA3AF] sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left w-8"></th>
                      <th className="px-2 py-1.5 text-left">Collection</th>
                      <th className="px-2 py-1.5 text-right">Live</th>
                      <th className="px-2 py-1.5 text-right">Snapshot</th>
                      <th className="px-2 py-1.5 text-right">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.collection} className="border-t border-[#F1F5F9]">
                        <td className="px-2 py-1.5">
                          <input type="checkbox" checked={selected.has(r.collection)}
                            onChange={() => toggle(r.collection)}
                            data-testid={`restore-col-${r.collection}`} />
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[11px]">{r.collection}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{r.live_count.toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{r.snapshot_count.toLocaleString()}</td>
                        <td className={`px-2 py-1.5 text-right font-mono ${r.delta > 0 ? 'text-emerald-700' : r.delta < 0 ? 'text-rose-600' : 'text-[#9CA3AF]'}`}>
                          {r.delta > 0 ? '+' : ''}{r.delta}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-rose-700 mb-1">
                  Type &quot;{RESTORE_PHRASE}&quot; exactly to confirm
                </label>
                <input type="text" value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  placeholder={RESTORE_PHRASE}
                  className="w-full px-2 py-1.5 border border-rose-300 rounded text-xs font-mono focus:border-rose-500 focus:outline-none"
                  data-testid="restore-confirm-phrase" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose}
                  className="text-xs px-3 py-1.5 text-[#4B5563] hover:text-[#0A0A0A]">
                  Cancel
                </button>
                <button onClick={handleRestore} disabled={!canRun}
                  className="text-xs px-3 py-1.5 bg-rose-600 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="restore-confirm-btn">
                  {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArchiveRestore className="w-3 h-3" />}
                  Restore {selected.size} collection{selected.size === 1 ? '' : 's'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// No default export here — BackupsPanel is exported above at line ~272.
